/* Assembling frames into an animated GIF, one frame at a time.
 *
 * WHY gifenc AND NOT A HAND-ROLLED ENCODER. Three GIFs were hand-written while
 * measuring the animation work and ALL THREE were malformed -- one missing the
 * sub-block terminator after its LZW data, so the stream ran on into the next
 * frame. Chrome decoded them anyway; ImageEngine refused all three as origin
 * images, which read exactly like a CDN that cannot process GIF and cost an
 * hour. CLAUDE.md records the rule that came out of it: do not hand-write a
 * GIF. An encoder is the same rule with more at stake.
 *
 * WHY gifenc AND NOT gif.js. gifenc runs in NODE, so tests/gif_assemble_test.mjs
 * drives this file with no browser at all -- which matters more here than
 * anywhere else on the site, because the browser suites cannot see this feature
 * work (tests/video_to_gif_test.sh explains why). gif.js is worker-based and
 * browser-only, and would have pushed every assertion into the one environment
 * that cannot make them. MIT, 216 KB installed, and jszip is already precedent
 * for bundling a library that writes a binary format.
 *
 * STREAMING IS THE POINT. add() quantises, writes and lets go. A hundred frames
 * at 640x360 kept as RGBA is about 92 MB; this holds one frame's pixels and the
 * bytes written so far. peakFrames() exists so a test can PROVE that rather
 * than trusting the comment.
 */
import { GIFEncoder, quantize, applyPalette } from "gifenc"

/* GIF stores at most 256 colours per frame. Fewer is smaller and visibly
 * worse on a photograph; this is the format's own ceiling rather than a
 * setting worth exposing. */
export const MAX_COLOURS = 256

/* Loop forever. Every animated GIF anybody shares does, and a GIF that plays
 * once reads as broken rather than as a choice. */
export const LOOP_FOREVER = 0

export function createGifSink(options) {
    const {
        width,
        height,
        delayCs = 10,
        colours = MAX_COLOURS,
        loop = LOOP_FOREVER,
    } = options || {}

    if (!(width > 0) || !(height > 0)) throw new Error("a GIF needs a size")

    const encoder = GIFEncoder()
    let count = 0
    let held = 0        // frames whose pixels this object is holding, ever
    let peak = 0
    let done = false

    return {
        /* imageData is a canvas ImageData, or anything with .data as RGBA.
         *
         * The delay is written in HUNDREDTHS, which is what the format stores.
         * gifenc takes milliseconds, so the conversion happens here and once:
         * planFrames() decided the hundredths, and multiplying by 10 at the
         * boundary is what keeps the encoded timing equal to the planned one. */
        add(imageData) {
            if (done) throw new Error("this GIF has already been finished")
            const rgba = imageData && imageData.data ? imageData.data : imageData
            if (!rgba || !rgba.length) throw new Error("a frame with no pixels")

            held++
            peak = Math.max(peak, held)

            const palette = quantize(rgba, colours)
            const indexed = applyPalette(rgba, palette)
            encoder.writeFrame(indexed, width, height, {
                palette,
                delay: delayCs * 10,
                // Every frame carries a full picture rather than a difference.
                // The CDN re-encodes this afterwards and does the inter-frame
                // work far better than we would: measured, it halves what goes
                // up. Differencing here would cost time to save bytes that are
                // about to be thrown away.
                dispose: -1,
                repeat: count === 0 ? loop : undefined,
            })

            count++
            held--          // the pixels are gone; only bytes remain
            return count
        },

        finish() {
            if (done) throw new Error("this GIF has already been finished")
            if (!count) throw new Error("a GIF with no frames")
            done = true
            encoder.finish()
            return encoder.bytes()
        },

        get frames() { return count },

        /* The most frames this sink ever held pixels for at once. It is 1 while
         * add() streams and would climb the moment somebody "optimised" this by
         * collecting frames first. tests/gif_assemble_test.mjs asserts it. */
        peakFrames() { return peak },
    }
}

/* The name a converted clip is saved under.
 *
 * The extension is REPLACED rather than appended: "holiday.mp4.gif" is what a
 * tool looks like when nobody thought about it, and the visitor is going to
 * upload this somewhere with a filename box. */
export function gifNameFor(name) {
    const base = String(name || "clip").replace(/\.[A-Za-z0-9]{1,5}$/, "")
    return (base || "clip") + ".gif"
}
